import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

// 숫자 변경 시 글자색만 깜박이는 컴포넌트
const FlashValue: React.FC<{ value: number | null | undefined; format: (v: number) => string; color?: string }> = ({ value, format, color }) => {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prevRef.current !== undefined && prevRef.current !== value) {
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    }
    prevRef.current = value ?? undefined;
  }, [value]);

  if (value == null) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>;

  return (
    <span style={{
      color: flash ? '#ffe066' : (color || 'rgba(255,255,255,0.8)'),
      fontWeight: 600,
      transition: 'color 0.1s',
      fontFamily: 'monospace',
    }}>
      {format(value)}
    </span>
  );
};

const WS_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const pnlColor = (v: number) => v >= 0 ? '#2ed573' : '#ff6b6b';
const fmt = (v: number) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface AccountStat {
  accountId: string;
  name: string;
  bBookCustomerPnl: number;
  bBookBrokerPnl: number;
  bBookTradeCount: number;
  bBookTotalLots: number;
  aBookNetPnl: number;
  aBookBrokerProfit: number;
  aBookLpCost: number;
  aBookSwitchCount: number;
  brokerTotalPnl: number;
  agentPnl: number;
  floatingPnl: number;
  bookType?: string;
}

const StatisticsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountStat[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/v1/statistics/broker-pnl');
      setAccounts(res.data.accounts || []);
      setSummary(res.data.summary || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchDetail = async (accountId: string) => {
    setDetailLoading(true);
    setSelectedAccount(accountId);
    try {
      const res = await api.get(`/v1/statistics/broker-pnl/detail?accountId=${accountId}`);
      setDetail(res.data);
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const [floatingMap, setFloatingMap] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 5000);

    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/api/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe('/topic/equity-updates', (msg) => {
          const data = JSON.parse(msg.body);
          if (data.accountId) {
            const newFloating = data.equity - data.balance;
            setFloatingMap(prev => ({ ...prev, [data.accountId]: newFloating }));
          }
        });
      },
    });
    client.activate();
    return () => { clearInterval(t); client.deactivate(); };
  }, []);

  const filtered = accounts.filter(acc =>
    acc.accountId.includes(search) || (acc.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>📊 계좌별 손익</h1>
          <p style={s.sub}>B북 기간 + A북 전환 수익 · 30초 자동 갱신</p>
        </div>
        <button style={s.refreshBtn} onClick={fetchData}>🔄 새로고침</button>
      </div>

      {/* 요약 카드 */}
      {summary && (
        <div style={s.grid3}>
          <div style={{ ...s.card, borderColor: 'rgba(46,213,115,0.3)' }}>
            <div style={s.cardLabel}>총 브로커 순손익</div>
            <div style={{ ...s.cardValue, color: pnlColor(summary.totalBrokerPnl) }}>{fmt(summary.totalBrokerPnl)}</div>
            <div style={s.cardDesc}>B북 수익 + A북 순손익</div>
          </div>
          <div style={{ ...s.card, borderColor: 'rgba(255,165,0,0.3)' }}>
            <div style={s.cardLabel}>Agent 비용 합계</div>
            <div style={{ ...s.cardValue, color: pnlColor(summary.totalAgentPnl) }}>{fmt(summary.totalAgentPnl || 0)}</div>
            <div style={s.cardDesc}>리베이트 지급 합계</div>
          </div>
          <div style={{ ...s.card, borderColor: 'rgba(102,126,234,0.3)' }}>
            <div style={s.cardLabel}>미실현 (Floating)</div>
            <div style={{ ...s.cardValue, color: pnlColor(-summary.totalFloating) }}>{fmt(-summary.totalFloating)}</div>
            <div style={s.cardDesc}>현재 오픈 포지션 기준</div>
          </div>
          <div style={{ ...s.card, borderColor: 'rgba(255,255,255,0.1)' }}>
            <div style={s.cardLabel}>등록 계좌 수</div>
            <div style={{ ...s.cardValue, color: '#667eea' }}>{summary.accountCount}</div>
            <div style={s.cardDesc}>모니터링 중</div>
          </div>
        </div>
      )}

      {/* 검색 */}
      <div style={{ marginBottom: '20px' }}>
        <input style={s.search} placeholder="🔍 계좌번호 또는 이름 검색"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* 계좌 테이블 */}
      <div style={s.tableWrap}>
        {loading ? <div style={s.loading}>로딩 중...</div> :
         filtered.length === 0 ? <div style={s.empty}>📭 등록된 계좌가 없습니다.</div> : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>계좌번호</th>
                <th style={s.th}>이름</th>
                <th style={s.th}>구분</th>
                <th style={s.th}>B북 브로커 수익<br/><span style={s.thSub}>고객 손실 * -1</span></th>
                <th style={s.th}>B북 거래수</th>
                <th style={s.th}>A북 전환<br/><span style={s.thSub}>횟수</span></th>
                        <th style={s.th}>A북 브로커 수익<br/><span style={s.thSub}>전환 시점 Floating</span></th>
                        <th style={s.th}>Agent 비용</th>
                        <th style={s.th}>LP 비용</th>
                        <th style={s.th}>브로커 순손익</th>
                <th style={s.th}>미실현</th>
                <th style={s.th}>상세</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((acc, idx) => (
                <React.Fragment key={acc.accountId}>
                  <tr style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                    <td style={s.td}><span style={s.accId}>{acc.accountId}</span></td>
                    <td style={s.td}><span style={s.name}>{acc.name || '-'}</span></td>
                    <td style={s.td}>
                      <span style={acc.bookType === 'A' ? s.badgeA : s.badgeB}>
                        {acc.bookType === 'A' ? '🔴 A북' : '🟢 B북'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.mono, color: pnlColor(acc.bBookBrokerPnl || 0), fontWeight: 600 }}>
                        {fmt(acc.bBookBrokerPnl || 0)}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={s.mono}>{acc.bBookTradeCount || 0}건</span>
                    </td>
                    <td style={s.td}>
                      <span style={{ color: (acc.aBookSwitchCount || 0) > 0 ? '#f7971e' : 'rgba(255,255,255,0.3)' }}>
                        {acc.aBookSwitchCount || 0}회
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.mono, color: pnlColor(acc.aBookBrokerProfit || 0), fontWeight: 600 }}>
                        {fmt(acc.aBookBrokerProfit || 0)}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.mono, color: '#ff6b6b' }}>
                        {acc.agentPnl ? fmt(-Math.abs(Number(acc.agentPnl))) : '$0.00'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.mono, color: '#ff6b6b' }}>
                        {acc.aBookLpCost ? fmt(-Math.abs(Number(acc.aBookLpCost))) : '$0.00'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.mono, color: pnlColor(acc.brokerTotalPnl || 0), fontWeight: 700, fontSize: '15px' }}>
                        {fmt(acc.brokerTotalPnl || 0)}
                      </span>
                    </td>
                    <td style={s.td}>
                      <FlashValue
                        value={floatingMap[acc.accountId] !== undefined ? floatingMap[acc.accountId] : (acc.floatingPnl || null)}
                        format={v => fmt(v)}
                        color={pnlColor(floatingMap[acc.accountId] !== undefined ? floatingMap[acc.accountId] : (acc.floatingPnl || 0))}
                      />
                    </td>
                    <td style={s.td}>
                      <button style={selectedAccount === acc.accountId ? { ...s.detailBtn, background: 'rgba(102,126,234,0.3)' } : s.detailBtn}
                        onClick={() => selectedAccount === acc.accountId ? (setSelectedAccount(null), setDetail(null)) : fetchDetail(acc.accountId)}>
                        {selectedAccount === acc.accountId ? '닫기 ✕' : '상세 →'}
                      </button>
                    </td>
                  </tr>
                  {selectedAccount === acc.accountId && (
                    <tr>
                      <td colSpan={12} style={{ padding: 0, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <div style={s.detailPanel}>
                          <div style={s.detailHeader}>
                            <h2 style={s.detailTitle}>계좌 {selectedAccount} 상세</h2>
                            <button style={s.closeBtn} onClick={() => { setSelectedAccount(null); setDetail(null); }}>✕</button>
                          </div>
                          {detailLoading ? <div style={s.loading}>로딩 중...</div> : detail && (
                            <div>
              {/* B북 요약 */}
              {detail.bBookSummary && (
                <div style={s.section}>
                  <h3 style={s.sectionTitle}>🟢 B북 기간 요약</h3>
                  <div style={s.summaryRow}>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>거래수</div>
                      <div style={s.summaryVal}>{detail.bBookSummary.tradeCount}건</div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>고객 손익</div>
                      <div style={{ ...s.summaryVal, color: pnlColor(detail.bBookSummary.customerPnl) }}>
                        {fmt(detail.bBookSummary.customerPnl)}
                      </div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>Agent 비용</div>
                      <div style={{ ...s.summaryVal, color: '#ff6b6b' }}>
                        {detail.bBookSummary.agentPnl ? fmt(-Math.abs(Number(detail.bBookSummary.agentPnl))) : '$0.00'}
                      </div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>브로커 수익</div>
                      <div style={{ ...s.summaryVal, color: pnlColor(detail.bBookSummary.brokerPnl) }}>
                        {fmt(detail.bBookSummary.brokerPnl)}
                      </div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>총 롯수</div>
                      <div style={s.summaryVal}>{Number(detail.bBookSummary.totalLots || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* B북 월별 손익 */}
              {detail.bBookMonthly?.length > 0 && (
                <div style={s.section}>
                  <h3 style={s.sectionTitle}>📅 B북 일별 손익</h3>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>일자</th>
                        <th style={s.th}>거래수</th>
                        <th style={s.th}>고객 손익</th>
                        <th style={s.th}>Agent 비용</th>
                        <th style={s.th}>브로커 수익</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.bBookMonthly.map((m: any, idx: number) => (
                        <tr key={m.month} style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                          <td style={s.td}><span style={s.mono}>{m.month}</span></td>
                          <td style={s.td}><span style={s.mono}>{m.trade_count}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: pnlColor(m.customer_pnl) }}>{fmt(m.customer_pnl)}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: '#ff6b6b' }}>{m.agent_pnl ? fmt(-Math.abs(Number(m.agent_pnl))) : '$0.00'}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: pnlColor(m.broker_pnl), fontWeight: 700 }}>{fmt(m.broker_pnl)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* A북 기간 요약 */}
              {detail.aBookHistory?.length > 0 && (
                <div style={s.section}>
                  <h3 style={s.sectionTitle}>🔴 A북 기간 요약</h3>
                  <div style={s.summaryRow}>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>전환 횟수</div>
                      <div style={s.summaryVal}>{detail.aBookHistory.length}회</div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>브로커 수익 합계</div>
                      <div style={{ ...s.summaryVal, color: pnlColor(detail.aBookHistory.reduce((sum: number, h: any) => sum + (Number(h.broker_profit) || 0), 0)) }}>
                        {fmt(detail.aBookHistory.reduce((sum: number, h: any) => sum + (Number(h.broker_profit) || 0), 0))}
                      </div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>LP 비용 합계</div>
                      <div style={{ ...s.summaryVal, color: '#ff6b6b' }}>
                        {fmt(-Math.abs(detail.aBookHistory.reduce((sum: number, h: any) => sum + (Number(h.lp_cost) || 0), 0)))}
                      </div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>Agent 비용 합계</div>
                      <div style={{ ...s.summaryVal, color: '#ff6b6b' }}>
                        {fmt(-Math.abs(detail.aBookHistory.reduce((sum: number, h: any) => sum + (Number(h.agent_pnl) || 0), 0)))}
                      </div>
                    </div>
                    <div style={s.summaryItem}>
                      <div style={s.summaryLabel}>순손익 합계</div>
                      <div style={{ ...s.summaryVal, color: pnlColor(detail.aBookHistory.reduce((sum: number, h: any) => sum + (Number(h.net_profit) || 0), 0)) }}>
                        {fmt(detail.aBookHistory.reduce((sum: number, h: any) => sum + (Number(h.net_profit) || 0), 0))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* A북 전환 이력 */}
              {detail.aBookHistory?.length > 0 && (
                <div style={s.section}>
                  <h3 style={s.sectionTitle}>🔴 A북 전환 이력 및 수익</h3>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>전환 시각</th>
                        <th style={s.th}>Balance</th>
                        <th style={s.th}>Equity</th>
                        <th style={s.th}>A북 전환 임계점</th>
                        <th style={s.th}>복귀 시각</th>
                        <th style={s.th}>복귀 방식</th>
                        <th style={s.th}>브로커 수익</th>
                        <th style={s.th}>LP 비용</th>
                        <th style={s.th}>Agent 비용</th>
                        <th style={s.th}>순손익</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.aBookHistory.map((h: any, idx: number) => (
                        <tr key={idx} style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                          <td style={s.td}><span style={s.mono}>{new Date(h.triggered_at).toLocaleString('ko-KR')}</span></td>
                          <td style={s.td}><span style={s.mono}>{h.balance_at_trigger != null ? fmt(Number(h.balance_at_trigger)) : '-'}</span></td>
                          <td style={s.td}><span style={s.mono}>{h.equity_at_trigger != null ? fmt(Number(h.equity_at_trigger)) : '-'}</span></td>
                          <td style={s.td}><span style={{ color: '#ff6b6b', fontFamily: 'monospace' }}>{h.drawdown_at_trigger != null ? `${Number(h.drawdown_at_trigger).toFixed(2)}%` : '-'}</span></td>
                          <td style={s.td}><span style={s.mono}>{h.returned_at ? new Date(h.returned_at).toLocaleString('ko-KR') : '-'}</span></td>
                          <td style={s.td}>
                            {h.return_type && (
                              <span style={{ color: h.return_type === 'AUTO' ? '#2ed573' : '#f7971e', fontSize: '12px' }}>
                                {h.return_type === 'AUTO' ? '자동' : '수동'}
                              </span>
                            )}
                          </td>
                          <td style={s.td}><span style={{ ...s.mono, color: pnlColor(h.broker_profit) }}>{h.broker_profit != null ? fmt(h.broker_profit) : '-'}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: '#ff6b6b' }}>{h.lp_cost ? fmt(-Math.abs(Number(h.lp_cost))) : '$0.00'}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: '#ff6b6b' }}>{h.agent_pnl ? fmt(-Math.abs(Number(h.agent_pnl))) : '$0.00'}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: pnlColor(h.net_profit), fontWeight: 700 }}>{h.net_profit != null ? fmt(h.net_profit) : '-'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 현재 오픈 포지션 */}
              {detail.positions?.length > 0 && (
                <div style={s.section}>
                  <h3 style={s.sectionTitle}>📌 현재 오픈 포지션</h3>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>심볼</th>
                        <th style={s.th}>방향</th>
                        <th style={s.th}>롯수</th>
                        <th style={s.th}>진입가</th>
                        <th style={s.th}>현재가</th>
                        <th style={s.th}>Floating</th>
                        <th style={s.th}>구분</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.positions.map((pos: any, idx: number) => (
                        <tr key={pos.ticket} style={idx % 2 === 0 ? s.trEven : s.trOdd}>
                          <td style={s.td}><span style={s.mono}>{pos.symbol}</span></td>
                          <td style={s.td}><span style={{ color: pos.action === 0 ? '#2ed573' : '#ff6b6b', fontWeight: 700 }}>{pos.action === 0 ? 'BUY' : 'SELL'}</span></td>
                          <td style={s.td}><span style={s.mono}>{pos.lots}</span></td>
                          <td style={s.td}><span style={s.mono}>{pos.open_price}</span></td>
                          <td style={s.td}><span style={s.mono}>{pos.current_price}</span></td>
                          <td style={s.td}><span style={{ ...s.mono, color: pnlColor(pos.profit) }}>{fmt(pos.profit)}</span></td>
                          <td style={s.td}><span style={pos.book_type === 'A' ? s.badgeA : s.badgeB}>{pos.book_type === 'A' ? 'A북' : 'B북'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { color: '#fff', width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, margin: '0 0 4px' },
  sub: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  refreshBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },
  card: { background: 'rgba(255,255,255,0.04)', border: '1px solid', borderRadius: '16px', padding: '20px' },
  cardLabel: { color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' },
  cardValue: { fontSize: '24px', fontWeight: 700, fontFamily: 'monospace', marginBottom: '4px' },
  cardDesc: { color: 'rgba(255,255,255,0.3)', fontSize: '11px' },
  search: { width: '100%', maxWidth: '400px', padding: '10px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none' },
  tableWrap: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'auto', marginBottom: '24px' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { padding: '12px 14px', textAlign: 'left' as const, fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' as const },
  thSub: { fontWeight: 400, color: 'rgba(255,255,255,0.3)', fontSize: '10px' },
  trEven: { background: 'transparent' },
  trOdd: { background: 'rgba(255,255,255,0.02)' },
  td: { padding: '12px 14px', fontSize: '13px', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' as const },
  accId: { fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: '#667eea' },
  name: { color: 'rgba(255,255,255,0.8)' },
  mono: { fontFamily: 'monospace', fontSize: '13px' },
  badgeA: { background: 'rgba(255,107,107,0.15)', color: '#ff6b6b', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 },
  badgeB: { background: 'rgba(46,213,115,0.15)', color: '#2ed573', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 },
  detailBtn: { background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.25)', borderRadius: '6px', padding: '5px 12px', color: '#667eea', fontSize: '12px', cursor: 'pointer', fontWeight: 600 },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  empty: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  detailPanel: { background: 'rgba(20,20,40,0.95)', border: 'none', borderRadius: '0', padding: '24px 24px 24px 24px' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  detailTitle: { fontSize: '18px', fontWeight: 700, margin: 0 },
  closeBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 12px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: '12px' },
  summaryRow: { display: 'flex', gap: '32px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '16px' },
  summaryItem: { display: 'flex', flexDirection: 'column' as const },
  summaryLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' },
  summaryVal: { fontSize: '16px', fontWeight: 700, fontFamily: 'monospace' },
};

export default StatisticsPage;
